import type { StrictSpacingStyle } from "../util/types";

import { Text } from "../Text";
import { useTheme, type Theme } from "../ThemeProvider";

import React, { useMemo, useState } from "react";
import {
  type PressableProps,
  Platform,
  Pressable,
  StyleSheet,
  View,
  type ViewStyle,
} from "react-native";

export interface RadioSelectorItemAccessibilityLabelProps {
  /**
   * announces position and length of item in the group. ex. 2 of 5
   */
  positionText?: string;

  /**
   * identifies to assistive technology users the radio groups overall group description
   */
  radioGroupDescription?: string;

  /**
   * associates the error message to the radio for the assistive technology user
   */
  errorText?: string;

  /**
   * identifies the radio control to be required
   * @default: required
   */
  requiredText?: string;

  /**
   * identifies the radio control to be invalid
   * @default: invalid
   */
  invalidText?: string;
}

export interface RadioSelectorItemProps extends Omit<PressableProps, "id"> {
  disabled?: boolean;
  error?: boolean;
  labelText: string;
  explainerText?: string;
  selected?: boolean;
  required?: boolean;
  onSelect: (id: number) => void;
  id?: number;
  styleModifier?: StrictSpacingStyle;
  /** When true, applies top border radius to match the list container. */
  isFirstItem?: boolean;
  /** When true, applies bottom border radius to match the list container. */
  isLastItem?: boolean;
  a11yLabels?: RadioSelectorItemAccessibilityLabelProps;
}

export type RadioSelectorItemRef = React.ElementRef<typeof Pressable>;

const getVariantStyle = (disabled: boolean, error: boolean, selected: boolean, theme: Theme) => {
  const variantColors = {
    default: {
      selected: theme.colors.foregroundActionStrong,
      unselected: theme.colors.foregroundActionStrong,
    },
    error: {
      selected: theme.colors.borderStatusCriticalStrong,
      unselected: theme.colors.borderStatusCriticalStrong,
    },
    disabled: {
      selected: theme.colors.foregroundActionStrong,
      unselected: theme.colors.foregroundActionStrong,
    },
  } as const;

  let variant: "default" | "error" | "disabled" = "default";

  if (disabled) {
    variant = "disabled";
  }

  // We want to fall through to the error variant if both are true
  // so make sure not to chance this to an else-if
  if (error) {
    variant = "error";
  }

  return variantColors[variant][selected ? "selected" : "unselected"];
};

const RadioSelectorItemInner = React.forwardRef<RadioSelectorItemRef, RadioSelectorItemProps>(
  function RadioSelectorItem(
    {
      disabled = false,
      error = false,
      labelText,
      explainerText,
      selected = false,
      required = false,
      onSelect,
      id,
      styleModifier,
      isFirstItem,
      isLastItem,
      a11yLabels,
      ...props
    },
    ref,
  ) {
    const theme = useTheme();
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const {
      positionText,
      radioGroupDescription,
      errorText,
      requiredText = "required",
      invalidText = "invalid",
    } = a11yLabels ?? {};

    /**
     * A11y: screenreader information that should read out radio position, the radio group label,
     * required status, error/invalid status, and error text
     * Ex. 2 of 5. Select a Job Type. required. invalid. error: select at least one job type
     */
    const accessibilityDescription = [
      `${positionText ?? ""}`,
      `${radioGroupDescription ?? ""}`,
      `${required ? requiredText : ""}`,
      `${error ? invalidText : ""}`,
      `${error && errorText ? errorText : ""}`,
    ].filter((txt) => txt !== "");
    const cornerRadius = theme.borderRadius.lg;
    const styles = useMemo(
      () =>
        StyleSheet.create({
          container: {
            display: "flex",
            flexDirection: "row",
            width: "100%",
            paddingTop: theme.spacing.s3,
            paddingBottom: theme.spacing.s3,
            paddingLeft: theme.spacing.s5,
            paddingRight: theme.spacing.s5,
          },
          firstItem: {
            borderTopLeftRadius: cornerRadius,
            borderTopRightRadius: cornerRadius,
          },
          lastItem: {
            borderBottomLeftRadius: cornerRadius,
            borderBottomRightRadius: cornerRadius,
          },
          selected: {
            backgroundColor: isHovered
              ? theme.colors.backgroundActionWeakHover
              : theme.colors.backgroundWeaker,
          },
          unselected: {
            backgroundColor: isHovered
              ? theme.colors.backgroundActionHover
              : theme.colors.backgroundAction,
          },
          outerCircle: {
            height: 24,
            width: 24,
            borderRadius: theme.borderRadius.full,
            justifyContent: "center",
            alignItems: "center",
            marginRight: theme.spacing.s3,
          },
          innerCircle: {
            height: 12,
            width: 12,
            borderRadius: theme.borderRadius.full,
          },
          labelBox: {
            flex: 1,
          },
        }),
      [theme, cornerRadius, isHovered],
    );

    const textColor = theme.colors.foregroundActionStrong;

    return (
      <Pressable
        ref={ref}
        role="radio"
        disabled={disabled}
        aria-checked={selected}
        accessibilityState={{ selected }}
        accessibilityRole="radio"
        accessibilityHint={accessibilityDescription.join(". ")}
        accessible={true}
        onPress={() => onSelect(id ?? 0)}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        style={[
          styles.container,
          styleModifier,
          isFirstItem && styles.firstItem,
          isLastItem && styles.lastItem,
          selected ? styles.selected : styles.unselected,
          Platform.OS === "web" &&
            ({
              outlineStyle: isFocused ? "solid" : "none",
              outlineWidth: isFocused ? 2 : undefined,
              outlineColor: isFocused ? theme.colors.utilityFocus : undefined,
              outlineOffset: isFocused ? -2 : undefined,
            } as unknown as ViewStyle),
        ]}
        {...props}
      >
        <View
          style={[
            styles.outerCircle,
            { borderColor: getVariantStyle(disabled, error, selected, theme) },

            { borderWidth: selected ? 2 : 1 },
          ]}
        >
          {selected && (
            <View
              style={[
                styles.innerCircle,
                { backgroundColor: getVariantStyle(disabled, error, selected, theme) },
              ]}
            />
          )}
        </View>

        <View style={styles.labelBox}>
          <Text level={3} sx={{ color: textColor, fontWeight: selected ? "bold" : "normal" }}>
            {labelText}
          </Text>

          {explainerText && (
            <Text level={2} sx={{ color: textColor }}>
              {explainerText}
            </Text>
          )}
        </View>
      </Pressable>
    );
  },
);

export const RadioSelectorItem = RadioSelectorItemInner;
